<template>
  <b-modal
    id="break-time-update-modal"
    cancel-variant="outline-secondary"
    :cancel-title="$t('button.close')"
    centered
    no-close-on-backdrop
    :title="$t(isEdit ? 'general.edit_break_time_rules' : 'general.add_break_time_rules')"
    :loading="loading"
    @ok="handleOk"
    @cancel="handleClose"
  >
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        class="btn"
        @click="handleClose"
      >
        {{ $t('button.close') }}
      </b-button>
      <n-button-loading
        :loading="loading"
        class="btn"
        variant="primary"
        :disabled="disabledSubmit"
        @submit="handleOk"
      >
        {{ $t('button.confirm') }}
      </n-button-loading>
    </template>
    <validation-observer
      ref="breakTimeForm"
      #default="{ invalid }"
    >
      <!-- form -->
      <n-form-confirmation
        ref="formConfirmation"
        key="breakTimeForm"
        :message="$t('general.are_you_sure_you_want_to_confirm_this_operation')"
        :confirm-button-text="`button.confirm`"
        :cancel-button-text="`button.no`"
        :form="$refs.breakTimeForm"
        :disabled="invalid"
        @submit="submit"
      >
        <div class="service-action">
          <b-form-group
            :label="$t('general.number_of_breaks')"
            label-for="break-times"
          >
            <b-form-spinbutton
              id="break-times"
              v-model="data.totalBreakTime"
              min="0"
              max="10"
              class="mb-1"
            />
            <div class="hint-text">
              <small style="color: #898989">{{
                $t('general.specify_breaks_per_day')
              }}</small>
            </div>
          </b-form-group>

          <b-form-group
            :label="$t('general.break_duration')"
            label-for="break-duration"
          >
            <b-form-spinbutton
              id="break-duration"
              v-model="data.breakDuration"
              :min="$route.query.dev === 'true' ? 1 : 0"
              max="60"
              :step="$route.query.dev === 'true' ? 1 : 5"
              class="mb-1"
            />
            <div class="hint-text">
              <small style="color: #898989">{{
                $t('general.set_duration')
              }}</small>
            </div>
          </b-form-group>
        </div>
      </n-form-confirmation>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BFormGroup, BFormSpinbutton,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import NFormConfirmation from '@/components/NFormConfirmation.vue'
import useJwt from '@/auth/jwt/useJwt'
import NButtonLoading from '@/components/NButtonLoading.vue'
import ToastificationAvatar from '@core/components/toastification/ToastificationAvatar.vue'

const breakTimeRepository = Repository.get('breakTime')

export default {
  components: {
    BModal,
    NFormConfirmation,
    BButton,
    NButtonLoading,
    BFormGroup,
    BFormSpinbutton,
  },
  data() {
    return {
      loading: false,
      data: {
        totalBreakTime: 0,
        breakDuration: 0,
        name: {},
        abilities: [],
      },
      roleId: '',
      isEdit: false,
      initBreakTime: {
        totalBreakTime: 0,
        breakDuration: 0,
      },
    }
  },
  computed: {
    userData() {
      return this.$store.state.profile.email
          || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    disabledSubmit() {
      // If either value is greater than 0, both must be greater than 0
      if ((this.data.totalBreakTime > 0 || this.data.breakDuration > 0)
          && (this.data.totalBreakTime <= 0 || this.data.breakDuration <= 0)) {
        return true
      }

      // Check if values are unchanged from initial state
      return this.data.totalBreakTime === this.initBreakTime.totalBreakTime
        && this.data.breakDuration === this.initBreakTime.breakDuration
    },
  },
  watch: {},
  methods: {
    handleClose() {
      this.hide()
    },
    show(role) {
      this.roleId = role.id
      this.data = {
        totalBreakTime: role.totalBreakTime || 0,
        breakDuration: role.breakDuration || 0,
      }
      this.initBreakTime = {
        totalBreakTime: role.totalBreakTime || 0,
        breakDuration: role.breakDuration || 0,
      }
      this.isEdit = !!(role.totalBreakTime || role.breakDuration)
      this.$bvModal.show('break-time-update-modal')
    },
    hide() {
      this.data = {
        totalBreakTime: 2,
        breakDuration: 15,
      }
      this.roleId = ''
      this.$bvModal.hide('break-time-update-modal')
    },
    handleOk(bvModal) {
      if (bvModal) bvModal.preventDefault()
      if (this.loading) return

      if (this.$refs.breakTimeForm?.validate) {
        this.$refs.breakTimeForm.validate().then(success => {
          if (success) {
            this.$refs.formConfirmation.confirm()
          }
        })
      } else {
        this.submit()
      }
    },
    async submit() {
      if (!this.roleId) return

      this.loading = true
      this.$emit('loading', true)
      try {
        const response = await breakTimeRepository.update(this.roleId, this.data)
        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('breadcrumb.breakTime'),
            icon: 'ClockIcon',
            variant: 'success',
            text: this.$t('alert.modifyBreakTimeSuccess'),
          },
        })
        this.hide()
        this.$emit('success', response)
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.breakTimeForm.setErrors(error.response?.data?.validate)
        }
      } finally {
        this.loading = false
        this.$emit('loading', false)
      }
    },
  },
  setup() {
    return {}
  },
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';

  .service-info {
    background-color: #f8f8f8;
    padding: 1rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;

    label {
      color: #6e6b7b;
      width: 160px;
      font-size: 1rem;
      margin-bottom: 0.75rem !important;
    }

    p {
      color: #5e5873;
      font-size: 1rem;
    }
  }

  .service-action {
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #eaeaea;
    margin-bottom: 0.5rem;

    label {
      color: #6e6b7b;
      font-size: 1rem;
      font-weight: 500;
    }
  }
</style>
